import React, { useCallback, useContext } from 'react';
import {
    autoCreateQuery as autoCreatePersonQuery,
    getPersonOption,
    client as personClient
} from '../../common/components/person/person';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormWidget from '../../common/form/FormWidget';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import {
    autoCreateQuery as autoCreateOrganizationQuery,
    client as organizationClient
} from '../../organization/organization';
import OrganizationOptionRenderer from '../../organization/OrganizationOptionRenderer';
import { getOption as getOrganizationOption, quickAddOrganization } from '../../organization/QuickAddOrganization';
import { quickAddPerson } from '../../person/helpers/QuickAddPerson';
import PersonOptionRenderer from '../../shortlists/form/PersonOptionRenderer';
import { ReferenceField } from '../attributes';
import { ReferenceEntityValue } from './referenceConstants';

type ReferenceValueEditorProps = {
    field: ReferenceField & {
        getOption?: (item) => object;
        notOnlyRef?: boolean;
    };
};

const getOptionRenderer = (entity: ReferenceEntityValue) => {
    switch (entity) {
        case ENTITY_CONSTANTS.person:
            return PersonOptionRenderer;
        case ENTITY_CONSTANTS.organization:
            return OrganizationOptionRenderer;
        default:
            return undefined;
    }
};

export default function ReferenceAttributeEditor({ field }: ReferenceValueEditorProps) {
    const { state, handleChange } = useContext(FormContext);
    const currentValue = state?.[field?.name];

    const isCreateSupported = (entity: ReferenceEntityValue | null | undefined) =>
        entity === ENTITY_CONSTANTS.person || entity === ENTITY_CONSTANTS.organization;

    const [savePerson] = useMutationWithContext(autoCreatePersonQuery, personClient);
    const [saveOrganization] = useMutationWithContext(autoCreateOrganizationQuery, organizationClient);

    const entity = field.entityToReference as ReferenceEntityValue;
    const saveMutation = entity === ENTITY_CONSTANTS.person ? savePerson : saveOrganization;
    const baseOnCreateHandle = entity === ENTITY_CONSTANTS.person ? quickAddPerson : quickAddOrganization;
    const getOptionFn =
        field.getOption || (entity === ENTITY_CONSTANTS.person ? getPersonOption : getOrganizationOption);

    const optimisticOnCreateHandle = useCallback(
        (value, save) => {
            if (!handleChange) {
                return baseOnCreateHandle(value, save);
            }

            const tempOption = {
                id: value.id || '',
                label: value.label,
                ref: value.ref || { id: '', label: value.label },
                createOption: true
            };

            if (field.isMultiSelect) {
                const updatedValue = Array.isArray(currentValue) ? [...currentValue, tempOption] : [tempOption];
                handleChange(field.name, updatedValue);
            } else {
                handleChange(field.name, tempOption);
            }

            const promise = baseOnCreateHandle(value, save);

            if (promise && typeof promise.then === 'function') {
                promise.then((createdValue) => {
                    if (field.isMultiSelect) {
                        const filteredValues = Array.isArray(currentValue)
                            ? currentValue.filter((item) => !item.createOption)
                            : [];
                        handleChange(field.name, [...filteredValues, createdValue]);
                    } else {
                        handleChange(field.name, createdValue);
                    }
                });
            }

            return promise;
        },
        [field.name, field.isMultiSelect, currentValue, handleChange, baseOnCreateHandle]
    );

    return (
        <FormContainer>
            <FormItem>
                <FormWidget
                    name={field.name}
                    component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                    entity={field.entityToReference}
                    label={field.label}
                    multiple={field.isMultiSelect}
                    OptionRenderer={getOptionRenderer(field.entityToReference as ReferenceEntityValue)}
                    hideHelperText
                    preload={false}
                    addOption={field.allowCreate && isCreateSupported(field.entityToReference as ReferenceEntityValue)}
                    saveMutation={field.allowCreate ? saveMutation : undefined}
                    onCreateHandle={field.allowCreate ? optimisticOnCreateHandle : undefined}
                    getOption={getOptionFn}
                    notOnlyRef={field.notOnlyRef}
                />
            </FormItem>
        </FormContainer>
    );
}
